import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import axios from "../../axios";
import Modal from "../Modal/Modal";
import Switcher from "../Switcher/Switcher";
import SimpleTooltip from "../SimpleTooltip/SimpleTooltip";
import useConfirmDelete from "../../hooks/useConfirmDelete";
import { showToastWithTimeout } from "../../store/actions/message";
import { updateTwoFactorAuthenticationRequired } from "../../store/actions/layout";

const IconButton = styled.button(
  ({ theme }) => `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: ${theme.gray[`gray${theme.type === "dark" ? 600 : 500}`]};
    line-height: 1;

    &:hover {
      color: ${theme.text.primary};
    }
  `
);

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 8px;
`;

export default function Mandatory2FAInfo() {
  const dispatch = useDispatch();
  const { tenantId, twoFactorAuthRequired } = useSelector((state) => ({
    tenantId: state.layout?.tenantId,
    twoFactorAuthRequired: Boolean(state.layout?.twoFactorAuthRequired),
  }));

  const [open, setOpen] = useState(false);

  const saveTwoFactorAuthRequired = useCallback(
    async (value) => {
      if (!tenantId) {
        showToastWithTimeout(
          "Tenant configuration is not loaded yet.",
          "danger"
        );
        return;
      }

      await axios.post(`/api/v1/tenant_config/${tenantId}`, {
        twoFactorAuthRequired: value,
      });

      const message = `Two-Factor-Authentication is now ${
        value ? "enabled" : "disabled"
      }.`;
      showToastWithTimeout(message, "success");
      dispatch(updateTwoFactorAuthenticationRequired(value));
    },
    [dispatch, tenantId]
  );

  const confirmDisableTwoFactorAuthentication = useConfirmDelete(
    () => saveTwoFactorAuthRequired(false),
    "Turn off enforced two-factor authentication?",
    <div>
      <p>
        Disabling this setting will stop enforcing multi-factor authentication
        for all users in this tenant.
      </p>
      <p>
        This reduces account security, though individual users will still be
        able to enable MFA for themselves from their profile page.
      </p>
    </div>,
    true,
    undefined,
    { confirmButtonTheme: "error" }
  );

  const modalBody = useMemo(() => {
    return (
      <>
        <p>
          Two-factor authentication is mandatory by default for all users since
          March 2026.
        </p>
        <p>As a tenant owner, you can disable this requirement for everyone.</p>

        <SwitchRow>
          <div style={{ fontWeight: 600 }}>Tenant setting</div>
          <Switcher
            handleSwitchChange={(evt) =>
              evt.currentTarget.checked
                ? saveTwoFactorAuthRequired(true)
                : confirmDisableTwoFactorAuthentication.setConfirming()
            }
            checked={twoFactorAuthRequired}
            rightLabel="Two Factor Auth Mandatory"
            size="small"
          />
        </SwitchRow>

        {confirmDisableTwoFactorAuthentication.confirming}
      </>
    );
  }, [
    confirmDisableTwoFactorAuthentication,
    saveTwoFactorAuthRequired,
    twoFactorAuthRequired,
  ]);

  return (
    <>
      <SimpleTooltip
        label="Click to see more info"
        usePortal
        style={{ display: "inline-flex" }}
      >
        <IconButton
          type="button"
          aria-label="More info about mandatory two-factor authentication"
          onClick={() => setOpen(true)}
        >
          <FontAwesomeIcon icon={["fas", "info-circle"]} size="sm" />
        </IconButton>
      </SimpleTooltip>

      {open ? (
        <Modal
          title="Mandatory Two-Factor Authentication"
          close={() => setOpen(false)}
          controlledFromOutside
          showClose
          width="640px"
        >
          {modalBody}
        </Modal>
      ) : null}
    </>
  );
}
